import React, { useEffect, useMemo, useRef, useState } from 'react';

import Cookie from 'js-cookie';
import throttle from 'just-throttle';
import qs from 'qs';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AnyAction, Dispatch } from 'redux';
import {
  OnboardingSubtitle,
  OnboardingTitle,
} from 'registration/containers/Onboarding';
import { OnboardingUpdates } from 'registration/types';

import {
  serverErrorsReceived,
  updateTalentProfileComplete,
  updateTechProfileComplete,
} from 'shared/actions/talentProfileActions';
import { Divider } from 'shared/components/ihcl/divider';
import { StatefulInput } from 'shared/components/ihcl/input';
import { styled } from 'shared/components/ihcl/styled';
import { toast } from 'shared/components/ihcl/v2/toast';
import sharedConstants from 'shared/constants/constants.json';
import getFetchSignupSettings from 'shared/helpers/getFetchSignupSettings';
import {
  trackClick,
  trackNonHiringSignUpCompletion,
} from 'shared/helpers/tracking';

import getGoogleClickID from '../../../helpers/getGoogleClickID';
import { convertStateToAttributes } from '../../../helpers/talentProfileConversions';
import validateTalentProfile, {
  validateTalentProfileField,
} from '../../../helpers/validateTalentProfile';
import { useOnboardingStepContext } from '../providers/OnboardingStepProvider';
import { useOnboardingVariantContext } from '../providers/OnboardingVariantProvider';
import Disclaimer from './Disclaimer';
import GoogleSignIn from './GoogleSignIn';
import {
  ErrorContainer,
  ErrorText,
  getTitles,
  SignInButton,
} from './OnboardingFormEmail';
import StickySubmitFooter from './StickySubmitFooter';

const DividerWrapper = styled('div', {
  marginTop: '24px',
  marginBottom: '24px',
});

type OnboardingFormEmailProps = {
  dispatch: Dispatch<AnyAction>;
  profileType?: 'nurse' | 'tech';
};
const OnboardingFormEmail = ({
  dispatch,
  profileType = 'nurse',
}: OnboardingFormEmailProps) => {
  const { addTalentProfileKeys, completeStep, localStepTalentProfile } =
    useOnboardingStepContext();
  const { currentStep, intent, onboardVariant } = useOnboardingVariantContext();

  const location = useLocation();
  const urlQuery = qs.parse(location.search, { ignoreQueryPrefix: true });

  const formRef = useRef(null);
  const {
    clearErrors,
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    defaultValues: localStepTalentProfile as any,
  });
  const formErrors = {
    ...errors,
    ...errors.user,
  };
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [googleJwt, setGoogleJwt] = useState<string>(null);

  useEffect(() => {
    addTalentProfileKeys(['email']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (formData) => {
    const validationErrors = validateTalentProfile(
      formData,
      Object.keys(formData)
    );
    if (validationErrors.size > 0 && !googleJwt) {
      return;
    }

    const type = profileType === 'tech' ? 'tech' : 'talent';

    const signUpUrl = `/users/sign_up/${type}.json`;

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let profileAttributes = {
      onboard_variant: onboardVariant,
      signup_intent: intent,
      timezone_from_onboarding: browserTimezone,
      type: profileType === 'tech' ? 'TechProfile' : 'NurseProfile',
    } as OnboardingUpdates;

    if (urlQuery.source) {
      profileAttributes.source = urlQuery.source as string;
    }
    sharedConstants.PROFILE_TRACKING_PARAM_NAMES.forEach((utmParamName) => {
      if (urlQuery[utmParamName]) {
        profileAttributes[utmParamName] = urlQuery[utmParamName];
      }
    });
    if (urlQuery.ref) {
      profileAttributes.referrer_code = urlQuery.ref as string;
    }
    const gclid = Cookie.get('gclid') || getGoogleClickID(urlQuery);
    if (gclid) {
      profileAttributes.google_click_id = gclid;
    }

    const localOnboardingUpdates = convertStateToAttributes(
      localStepTalentProfile,
      Object.keys(localStepTalentProfile),
      formData
    );

    profileAttributes = {
      ...profileAttributes,
      ...localOnboardingUpdates,
      current_onboard_step: currentStep,
    };
    const body = {
      user: {
        ...formData,
        talent_profile_attributes: profileAttributes,
        remember_me: true,
      },
    } as any;

    if (!formData || !formData.password) {
      body.gen_password = true;
    }
    if (googleJwt) {
      body.google = { credential: googleJwt };
    }
    setIsFormSubmitting(true);
    fetch(signUpUrl, getFetchSignupSettings(body))
      .then((data) => data.json())
      .then(async (json) => {
        setIsFormSubmitting(false);
        if (json && json.errors) {
          Object.keys(json.errors).forEach((key) => {
            if (key === 'user.email') {
              setError('email', {
                type: 'validationError',
                message: json.errors[key].join('; '),
              });
            } else if (key === 'google') {
              setError(key, {
                type: 'validationError',
                message: json.errors[key].join('; '),
              });
            }
          });
          dispatch(serverErrorsReceived(json));
        } else {
          trackClick('talent.sign_up', {
            Intent: intent,
          });
          trackNonHiringSignUpCompletion(profileAttributes);
          if (json.user_id && json.user_id.toString()) {
            mixpanel.alias(json.user_id.toString());
          } else {
            console.error(`cannot alias user_id: ${json.user_id}`);
          }
          mixpanel.register({ 'User Type': 'talent' });
          if (localStepTalentProfile.type === 'TechProfile') {
            dispatch(updateTechProfileComplete(json));
          } else {
            dispatch(updateTalentProfileComplete(json));
          }
          dispatch(serverErrorsReceived({}));
          completeStep({});
        }
      })
      .catch((error) => {
        console.error(error);
        setIsFormSubmitting(false);
        const errorMsg =
          'Oops, there was a problem connecting ' +
          'to the server. Please check your internet connection ' +
          'and try again, or refresh the page.';
        toast.error(errorMsg);
      });
  };

  useEffect(() => {
    if (googleJwt) {
      clearErrors('email');
      signUp({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleJwt]);

  const blurField = (evt, fieldName, fieldProps) => {
    fieldProps.onBlur(evt);
  };
  const changeField = (evt, fieldName, fieldProps) => {
    if (formErrors[fieldName]) {
      clearErrors(fieldName);
    }
    fieldProps.onChange(evt);
  };
  const emailProps = register('email', {
    validate: (value) => {
      if (!googleJwt) {
        return validateTalentProfileField('email', value);
      }
      return null;
    },
  });
  const submitHandler = handleSubmit(signUp);
  const throttledSubmitHandler = useMemo(
    () => throttle((evt) => submitHandler(evt), 1000, { leading: true }),
    []
  );
  const invalidEmailErrorMessage = formErrors.email?.message;
  const emailInputErrorMessage = !googleJwt && invalidEmailErrorMessage;
  const googleErrorMessage =
    googleJwt && (formErrors.google?.message || invalidEmailErrorMessage);
  const signInPrompt = invalidEmailErrorMessage ===
    'has already been taken' && <SignInButton />;
  const emailInput = (
    <StatefulInput
      caption={emailInputErrorMessage}
      collapseCaptionPadding
      initialState={{ value: localStepTalentProfile.email }}
      label="Email address"
      type="email"
      inputMode="email"
      {...emailProps}
      onBlur={(evt) => blurField(evt, 'email', emailProps)}
      onChange={(evt) => {
        setGoogleJwt(null);
        clearErrors('google');
        changeField(evt, 'email', emailProps);
      }}
      error={!!formErrors.email}
    />
  );
  const displayGoogleLogin = !!window.IH.googleOAuthClientId;
  const googleEmailButton = displayGoogleLogin ? (
    <>
      <GoogleSignIn setGoogleJwt={setGoogleJwt} isSignIn={false} />
      {googleErrorMessage && (
        <>
          <ErrorText>{googleErrorMessage}</ErrorText>
          {signInPrompt}
        </>
      )}
    </>
  ) : null;

  const [title, subtitle] = getTitles(intent, profileType);
  return (
    <>
      <div>
        <ErrorContainer errors={errors} />
        <OnboardingTitle>{title}</OnboardingTitle>
        <OnboardingSubtitle $fontWeight="500">{subtitle}</OnboardingSubtitle>
        <form
          action="#"
          id="OnboardingFormEmail"
          onSubmit={(evt) => {
            evt.preventDefault();
            throttledSubmitHandler(evt);
          }}
          ref={formRef}
        >
          {googleEmailButton}
          {displayGoogleLogin && (
            <DividerWrapper>
              <Divider>Or</Divider>
            </DividerWrapper>
          )}
          {emailInput}
          {emailInputErrorMessage && signInPrompt}
        </form>
      </div>
      <Disclaimer />
      <StickySubmitFooter
        form="OnboardingFormEmail"
        disabled={isFormSubmitting || (!googleJwt && Boolean(formErrors.email))}
      />
    </>
  );
};

export default connect()(OnboardingFormEmail);
